import { NumericFormat } from "react-number-format";
import { GenericField, InputProps } from "../Fields/GenericField";
import {
  changeCurrencyToCents,
  changeCurrencyToDollars,
  formatCurrency,
  getCurrencySymbol,
} from "@/lib/Formatters/formatCurrency";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { Currency } from "@/gql/graphql";
import { useField } from "formik";
import { cn } from "@/lib/utils";
import { clsx } from "clsx";
import { Input } from "@headlessui/react";
import { useTranslations } from "use-intl";
import { selectOnFocus } from "@/lib/Components/Form/Utils/selectOnFocus";

type CurrencyInputProps = InputProps & {
  currency: Currency;
  onChange?: (val: number | null) => void;
  suffix?: string;
  disabled?: boolean;
};

export function CurrencyInput({
  currency,
  onChange,
  suffix,
  autoFocus,
  disabled,
  ...props
}: CurrencyInputProps) {
  const t = useTranslations("common.form");
  const [field, { error, touched }, helpers] = useField(props.name);
  const { name, value } = field;
  const { setValue } = helpers;
  const showError = !!error && touched;

  return (
    <GenericField
      {...props}
      viewNode={
        <span>
          {formatCurrency(value, currency)}
          {suffix ? ` ${suffix}` : null}
        </span>
      }
    >
      <span
        data-disabled={disabled ? true : undefined}
        className={clsx([
          // Basic layout
          "relative block w-full",
          // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
          "before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-white before:shadow-sm",
          // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
          "dark:before:hidden",
          // Focus ring
          "after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent sm:focus-within:after:ring-2 sm:focus-within:after:ring-blue-500",
          // Disabled state
          "has-data-disabled:opacity-50 has-data-disabled:before:bg-zinc-950/5 has-data-disabled:before:shadow-none",
          // Invalid state
          "has-data-invalid:before:shadow-red-500/10",
        ])}
      >
        <Input
          as={NumericFormat}
          name={name}
          onBlur={field.onBlur}
          onFocus={selectOnFocus}
          autoFocus={autoFocus}
          disabled={disabled}
          data-disabled={disabled ? true : undefined}
          className={cn([
            // Basic layout
            "relative block w-full appearance-none rounded-lg px-[calc(--spacing(3.5)-1px)] py-[calc(--spacing(2.5)-1px)] sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(1.5)-1px)]",
            // Typography
            "text-base/6 text-zinc-950 placeholder:text-zinc-500 sm:text-sm/6 dark:text-white",
            // Border
            "data-hover:border-zinc-950/20 dark:data-hover:border-white/20 border border-zinc-950/10 dark:border-white/10",
            // Background color
            "bg-transparent dark:bg-white/5",
            // Hide default focus styles
            "focus:outline-hidden",
            // Invalid state
            "data-invalid:border-red-500 data-invalid:data-hover:border-red-500 dark:data-invalid:border-red-500 dark:data-invalid:data-hover:border-red-500",
            // Disabled state
            "data-disabled:border-zinc-950/20 dark:data-hover:data-disabled:border-white/15 dark:data-disabled:border-white/15 dark:data-disabled:bg-white/2.5",
            // Add padding for suffix
            suffix && "pr-12",
          ])}
          placeholder={t("currencyPlaceholder")}
          prefix={getCurrencySymbol(currency)}
          value={
            field.value === null
              ? ""
              : changeCurrencyToDollars(field.value, currency)
          }
          onValueChange={async (values) => {
            const newVal =
              values.floatValue === undefined
                ? null
                : changeCurrencyToCents(values.floatValue, currency);

            await setValue(newVal); //Round the value to avoid floating point errors
            onChange?.(newVal);
          }}
        />
        {suffix ? (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <span className="text-sm text-zinc-500">{suffix}</span>
          </div>
        ) : null}
        {showError && !suffix ? (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <ExclamationCircleIcon
              className="h-5 w-5 text-red-500"
              aria-hidden="true"
            />
          </div>
        ) : null}
      </span>
    </GenericField>
  );
}
